import {
  DateValue,
  DateValueEstimate,
  SurveyQuestionTypeOptions,
} from '@chiroup/core/types/Survey.type';
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import DatepickerDays from './DatepickerDays';
import DatepickerDaysOfWeek from './DatepickerDaysOfWeek';
import DatepickerEstimate from './DatepickerEstimate';
import MonthYearDialog from './MonthYearDialog';

type Props = {
  options?: SurveyQuestionTypeOptions;
  onChange: (val?: DateValue) => void;
  primaryColor: string;
  language?: string;
  size?: 'normal' | 'small';
  hideSelectedMessage?: boolean;
  initialValue?: DateValue;
  noShadow?: boolean;
};

export const HCDatepicker: React.FC<Props> = ({
  options = { allowEstimate: false, pastOnly: false, futureOnly: false },
  onChange,
  primaryColor,
  language = 'en',
  size = 'normal',
  hideSelectedMessage = false,
  initialValue,
  noShadow = false,
}) => {
  const startOfMonth = dayjs().startOf('month');
  const [viewDate, setViewDate] = useState(startOfMonth);
  const [skipDays, setSkipDays] = useState(startOfMonth.day());
  const [selectedDate, setSelectedDate] = useState<Dayjs>();
  const [exactDate, setExactDate] = useState(() => !initialValue?.estimate);
  console.log('exactDate', exactDate);
  console.log('initialValue', initialValue);
  const [monthDialogOpen, setMonthDialogOpen] = useState<boolean>(false);
  const [estimate, setEstimate] = useState<DateValueEstimate>(
    initialValue?.estimate || {
      unit: 'day',
      value: 1,
    }
  );
  // Initialize selected date and estimate from initialValue
  useEffect(() => {
    if (initialValue?.exact) {
      const parsed = dayjs(initialValue.exact, 'MM/DD/YYYY');

      if (parsed.isValid()) {
        setSelectedDate(parsed);
        setViewDate(parsed.startOf('month'));
      }

      setExactDate(true);
    }

    if (initialValue?.estimate) {
      setEstimate(initialValue.estimate);
      setExactDate(false);
    }
  }, [initialValue]);

  useEffect(() => {
    setSkipDays(viewDate.day());
  }, [viewDate]);

  const prevMonth = () => {
    setViewDate(prev => prev.subtract(1, 'month'));
  };

  const nextMonth = () => {
    setViewDate(prev => prev.add(1, 'month'));
  };

  const prevYear = () => {
    setViewDate(prev => prev.subtract(1, 'year'));
  };

  const nextYear = () => {
    setViewDate(prev => prev.add(1, 'year'));
  };

  const setMonthYear = (month: number, year: number) => {
    setViewDate(viewDate.set('month', month).set('year', year));
  };

  const selectDate = (day: number) => {
    const newDate = viewDate.set('date', day);
    setSelectedDate(newDate);
    onChange({ exact: newDate.format('MM/DD/YYYY') });
  };

  const selectExactDate = (val: boolean, e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setSelectedDate(undefined);
    setExactDate(val);
    onChange(val ? undefined : { estimate });
  };

  const onChangeEstimate = useCallback(
    (val: DateValueEstimate) => {
      setEstimate(val);
      onChange({ estimate: val });
    },
    [onChange]
  );

  // Size-based styling variables
  const isSmall = size === 'small';
  const calendarSize = isSmall ? 'w-full max-w-xs' : 'w-full max-w-96';
  const calendarTextSize = isSmall ? 'text-xs' : 'text-base sm:text-lg';
  const calendarPadding = isSmall ? 'p-1.5' : 'p-3 sm:p-4';
  const toggleMargin = isSmall ? 'mb-2' : 'mb-6';
  const toggleButtonPadding = isSmall ? 'py-1 px-4' : 'py-2 sm:px-8';
  const toggleButtonTextSize = isSmall ? 'text-xs' : 'text-sm';
  const navButtonSize = isSmall ? 'h-6 w-6 p-0.5' : 'h-8 w-8 p-1';
  const monthYearPadding = isSmall ? 'p-0.5 px-2 text-xs' : 'p-1 px-3';
  const gridGap = isSmall ? 'gap-1' : 'gap-4';
  const headerPadding = isSmall ? 'pb-1' : 'pb-2';

  return (
    <div>
      {options.allowEstimate && (
        <div
          className="font-small sm:align-center sm:flex sm:flex-col"
          onClick={e => e.stopPropagation()}
        >
          <div
            className={`relative ${toggleMargin} flex self-center rounded-lg bg-gray-300 dark:bg-dark-gray-500 p-0.5`}
          >
            <button
              onClick={e => selectExactDate(true, e)}
              onPointerDown={e => e.stopPropagation()}
              type="button"
              className={[
                `relative w-1/2 border-gray-300 dark:border-gray-600 rounded-md ${toggleButtonPadding} ${toggleButtonTextSize} leading-5 text-gray-700 dark:text-gray-400 whitespace-nowrap hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:border-blue-300 focus:ring-blue focus:z-10 active:bg-gray-50 dark:active:bg-dark-gray-700 active:text-gray-800 dark:active:text-gray-100 transition ease-in-out duration-150`,
                exactDate ? 'bg-white dark:bg-dark-gray-700 shadow-sm' : '',
              ].join(' ')}
            >
              {language === 'es' ? 'Fecha Exacta' : 'Exact date'}
            </button>
            <button
              onClick={e => selectExactDate(false, e)}
              onPointerDown={e => e.stopPropagation()}
              type="button"
              className={[
                `relative w-1/2 border-gray-300 dark:border-gray-600 rounded-md ${toggleButtonPadding} ${toggleButtonTextSize} leading-5 text-gray-700 dark:text-gray-400 whitespace-nowrap hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:border-blue-300 focus:ring-blue focus:z-10 active:bg-gray-50 dark:active:bg-dark-gray-700 active:text-gray-800 dark:active:text-gray-100 transition ease-in-out duration-150`,
                exactDate ? '' : 'bg-white dark:bg-dark-gray-700 shadow-sm',
              ].join(' ')}
              data-cy="estimate"
            >
              {language === 'es' ? 'Fecha Estimada' : 'Estimate'}
            </button>
          </div>
        </div>
      )}
      {exactDate ? (
        <div>
          {monthDialogOpen ? (
            <MonthYearDialog
              selectedDate={selectedDate ?? dayjs()}
              closeMonthDialog={() => setMonthDialogOpen(false)}
              setMonthYear={setMonthYear}
              primaryColor={primaryColor}
            />
          ) : (
            <div
              className={`rounded-lg bg-white dark:bg-dark-gray-600 ${calendarPadding} ${calendarTextSize} ${isSmall ? '' : 'shadow-xl'} ${calendarSize}`}
            >
              <div className={`flex justify-between ${headerPadding}`}>
                <div className="flex flex-row">
                  <button
                    onClick={prevYear}
                    type="button"
                    className={`${navButtonSize} rounded-full border-2 border-transparent text-gray-600 dark:text-gray-400 transition duration-150 ease-in-out hover:bg-gray-100 dark:hover:bg-dark-gray-500 hover:text-gray-900 dark:hover:text-gray-100 focus:bg-gray-100 dark:focus:bg-dark-gray-500 focus:text-gray-900 dark:focus:text-gray-100 focus:outline-none`}
                    aria-label="Previous year"
                  >
                    <ChevronDoubleLeftIcon />
                  </button>
                  <button
                    onClick={prevMonth}
                    type="button"
                    className={`${navButtonSize} rounded-full border-2 border-transparent text-gray-600 dark:text-gray-400 transition duration-150 ease-in-out hover:bg-gray-100 dark:hover:bg-dark-gray-500 hover:text-gray-900 dark:hover:text-gray-100 focus:bg-gray-100 dark:focus:bg-dark-gray-500 focus:text-gray-900 dark:focus:text-gray-100 focus:outline-none`}
                    aria-label="Previous month"
                  >
                    <ChevronLeftIcon />
                  </button>
                </div>
                <div
                  className={`cursor-pointer place-self-center ${monthYearPadding} rounded-full text-gray-600 dark:text-gray-400 transition duration-150 ease-in-out hover:bg-gray-100 dark:hover:bg-dark-gray-500 hover:text-gray-900 dark:hover:text-gray-100 focus:bg-gray-100 dark:focus:bg-dark-gray-500 focus:text-gray-900 dark:focus:text-gray-100 focus:outline-none`}
                  onClick={() => setMonthDialogOpen(true)}
                >
                  {isSmall ? viewDate.format('MMM YYYY') : viewDate.format('MMMM YYYY')}
                </div>
                <div className="flex flex-row">
                  <button
                    onClick={nextMonth}
                    type="button"
                    className={`${navButtonSize} rounded-full border-2 border-transparent text-gray-600 dark:text-gray-400 transition duration-150 ease-in-out hover:bg-gray-100 dark:hover:bg-dark-gray-500 hover:text-gray-900 dark:hover:text-gray-100 focus:bg-gray-100 dark:focus:bg-dark-gray-500 focus:text-gray-900 dark:focus:text-gray-100 focus:outline-none`}
                    aria-label="Next month"
                  >
                    <ChevronRightIcon />
                  </button>
                  <button
                    onClick={nextYear}
                    type="button"
                    className={`${navButtonSize} rounded-full border-2 border-transparent text-gray-600 dark:text-gray-400 transition duration-150 ease-in-out hover:bg-gray-100 dark:hover:bg-dark-gray-500 hover:text-gray-900 dark:hover:text-gray-100 focus:bg-gray-100 dark:focus:bg-dark-gray-500 focus:text-gray-900 dark:focus:text-gray-100 focus:outline-none`}
                    aria-label="Next year"
                  >
                    <ChevronDoubleRightIcon />
                  </button>
                </div>
              </div>
              <div className={`grid grid-cols-7 ${gridGap}`}>
                <DatepickerDaysOfWeek size={size} />
                <DatepickerDays
                  days={viewDate.daysInMonth()}
                  skipDays={skipDays}
                  selectDate={selectDate}
                  selectedDate={selectedDate}
                  viewDate={viewDate}
                  pastOnly={options.pastOnly}
                  futureOnly={options.futureOnly}
                  primaryColor={primaryColor}
                  size={size}
                />
              </div>
            </div>
          )}
          {!hideSelectedMessage && (
            <div className="pb-4">
              {selectedDate && (
                <div className="pt-6 text-2xl font-extrabold leading-10 tracking-tight text-gray-900 sm:text-3xl sm:leading-none">
                  <div>{language === 'es' ? 'Haz seleccionado' : "You've selected"}</div>
                  <div
                    className="pt-1"
                    style={{
                      color: primaryColor,
                    }}
                  >
                    {' '}
                    {selectedDate.format('MMMM D, YYYY')}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <DatepickerEstimate
          value={estimate}
          onChange={onChangeEstimate}
          primaryColor={primaryColor}
          language={language}
          size={size}
        />
      )}
    </div>
  );
};
